import { useEffect, useMemo, useState } from 'react'

import { api } from '../api/client'
import type { EagleCI, EagleParam, EagleScelta } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime, timeAgo } from './common'
import { EagleAssegnazione } from './EagleAssegnazione'
import { EagleNuovo } from './EagleNuovo'
import { EaglePunti } from './EaglePunti'
import { DataTable } from './DataTable'
import { StatoBadge, etichettaStato } from './EagleStato'
import { SerieParametro } from './SerieParametro'
import {
  GRUPPI,
  cosaMisura,
  etichettaFrequenza,
  etichettaMetodo,
  etichettaParametro,
  formatValore,
  motivo,
  profilo,
  soglia,
} from './eagleTesti'

/**
 * Eagle nella scheda del CI (ADR-0022, §5bis).
 *
 * Spento: si sceglie fra il **profilo predefinito** della classe (un clic) e la
 * **scelta manuale** (si spuntano i parametri e se ne imposta la frequenza).
 * Acceso: i parametri scelti con metodo, frequenza, soglie e stato; «Modifica»
 * rende la tabella modificabile, «Mostra tutti» fa vedere anche ciò che non è
 * scelto. I parametri che non si possono leggere restano in elenco con il
 * motivo, invece di sparire.
 */
export function SezioneEagle({ ciId, puoModificare }: { ciId: string; puoModificare: boolean }) {
  const stato = useAsync(() => api.ciEagle(ciId), [ciId])
  const [dati, setDati] = useState<EagleCI | null>(null)
  useEffect(() => {
    if (stato.data) setDati(stato.data)
  }, [stato.data])

  if (stato.loading && !dati) return <Spinner />
  if (!dati || !dati.licensed) return null
  return dati.enabled ? (
    <Attivo ciId={ciId} dati={dati} onDati={setDati} puoModificare={puoModificare} onRicarica={stato.reload} />
  ) : (
    <Attivazione ciId={ciId} dati={dati} onDati={setDati} puoModificare={puoModificare} />
  )
}

// ------------------------------------------------------------- attivazione

function Attivazione({
  ciId, dati, onDati, puoModificare,
}: { ciId: string; dati: EagleCI; onDati: (d: EagleCI) => void; puoModificare: boolean }) {
  const params = dati.params ?? []
  const prof = profilo(dati.profile?.key ?? 'base')
  const [modo, setModo] = useState<'profile' | 'manual'>('profile')
  const [frequenzaUnica, setFrequenzaUnica] = useState<string>('')
  // La scelta manuale parte da ciò che era scelto l'ultima volta (se il
  // monitoraggio era stato spento), altrimenti vuota.
  const [scelti, setScelti] = useState<Record<string, number>>(() =>
    Object.fromEntries(params.filter((p) => p.selected && p.available).map((p) => [p.key, p.interval_s])),
  )
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)
  const intervalli = dati.intervals ?? []
  const leggibiliDelProfilo = params.filter((p) => p.in_profile)

  async function attiva() {
    setInvio(true)
    setErrore(null)
    try {
      const nuovi =
        modo === 'profile'
          ? await api.ciEagleAttiva(ciId, { mode: 'profile', interval_s: frequenzaUnica ? Number(frequenzaUnica) : null })
          : await api.ciEagleAttiva(ciId, {
              mode: 'manual',
              params: Object.entries(scelti).map(([key, interval_s]) => ({ key, enabled: true, interval_s })),
            })
      onDati(nuovi)
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  return (
    <Card title={t('Eagle · monitoraggio')}>
      <p style={{ marginTop: 0 }}>
        {t('Monitora questo CI con le credenziali e i dati che CENSO ha già: scegli il profilo predefinito della sua classe oppure spunta tu i parametri e la frequenza.')}
      </p>
      <ErrorBanner error={errore} />
      <div className="eagle-scelta" role="radiogroup" aria-label={t('Come attivare il monitoraggio')}>
        <label className={`eagle-opzione${modo === 'profile' ? ' eagle-opzione--on' : ''}`}>
          <input type="radio" name={`eagle-modo-${ciId}`} checked={modo === 'profile'} onChange={() => setModo('profile')} />
          <span>
            <strong>{t('Profilo predefinito: {profilo}', { profilo: prof.label })}</strong>
            <span className="muted">{prof.descrizione}</span>
            <span className="muted">{t('{n} parametri su questo CI', { n: leggibiliDelProfilo.length })}</span>
          </span>
        </label>
        <label className={`eagle-opzione${modo === 'manual' ? ' eagle-opzione--on' : ''}`}>
          <input type="radio" name={`eagle-modo-${ciId}`} checked={modo === 'manual'} onChange={() => setModo('manual')} />
          <span>
            <strong>{t('Scelgo io')}</strong>
            <span className="muted">{t('Spunta i parametri da monitorare e imposta la frequenza di ciascuno.')}</span>
          </span>
        </label>
      </div>

      {modo === 'profile' ? (
        <>
          <label className="field" style={{ maxWidth: 320 }}>
            <span className="field__label">{t('Frequenza')}</span>
            <select value={frequenzaUnica} onChange={(e) => setFrequenzaUnica(e.target.value)}>
              <option value="">{t('Quella predefinita di ogni parametro')}</option>
              {intervalli.map((s) => (
                <option key={s} value={s}>{etichettaFrequenza(s)}</option>
              ))}
            </select>
          </label>
          <ElencoParametri params={leggibiliDelProfilo} />
        </>
      ) : (
        <>
          <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="secondary"
                    onClick={() => setScelti(Object.fromEntries(leggibiliDelProfilo.map((p) => [p.key, p.interval_s])))}>
              {t('Parti dal profilo')}
            </button>
            <button type="button" className="secondary" onClick={() => setScelti({})}>{t('Togli tutto')}</button>
          </p>
          <TabellaScelta
            params={params}
            intervalli={intervalli}
            scelti={scelti}
            onCambia={setScelti}
          />
        </>
      )}

      {puoModificare ? (
        <p style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 0 }}>
          <button type="button" onClick={attiva}
                  disabled={invio || (modo === 'manual' ? Object.keys(scelti).length === 0 : leggibiliDelProfilo.length === 0)}>
            {invio ? t('Attivo…') : t('Attiva il monitoraggio')}
          </button>
          <span className="muted">{t('CI monitorati in questo tenant: {n}', { n: dati.monitored_cis ?? 0 })}</span>
        </p>
      ) : (
        <p className="muted">{t('Il tuo ruolo può vedere Eagle ma non attivarlo.')}</p>
      )}
    </Card>
  )
}

/** Il profilo, in sola lettura: che cosa accende. */
function ElencoParametri({ params }: { params: EagleParam[] }) {
  if (params.length === 0) {
    return <p className="muted">{t('Il profilo non ha parametri leggibili su questo CI: usa «Scelgo io» per vedere perché.')}</p>
  }
  return (
    <ul className="eagle-elenco">
      {params.map((p) => (
        <li key={p.key}>
          <strong>{etichettaParametro(p)}</strong>
          <span className="muted"> · {etichettaMetodo(p)} · {etichettaFrequenza(p.interval_s)}</span>
        </li>
      ))}
    </ul>
  )
}

/** A parameter's name, detail, what it measures and why it cannot be read, as plain text. */
function testoParametro(p: EagleParam): string {
  return [etichettaParametro(p), p.detail, cosaMisura(p), motivo(p)].filter(Boolean).join(' ')
}

/** The parameter cell shared by the two tables. */
function CellaParametro({ p }: { p: EagleParam }) {
  return (
    <>
      <strong>{etichettaParametro(p)}</strong>
      {p.detail && <span className="muted"> · {p.detail}</span>}
      {cosaMisura(p) && <div className="muted eagle-piccolo">{cosaMisura(p)}</div>}
      {motivo(p) && <div className="eagle-motivo">{motivo(p)}</div>}
    </>
  )
}

function TabellaScelta({
  params, intervalli, scelti, onCambia,
}: {
  params: EagleParam[]
  intervalli: number[]
  scelti: Record<string, number>
  onCambia: (s: Record<string, number>) => void
}) {
  return (
    <>
      {GRUPPI.map((g) => {
        const righe = params.filter((p) => p.group === g.key)
        if (righe.length === 0) return null
        return (
          <div key={g.key} className="eagle-gruppo">
            <h4>{g.label()}</h4>
            <DataTable
              rows={righe}
              rowKey={(p) => p.key}
              rowClassName={(p) => (p.available ? undefined : 'eagle-riga--spenta')}
              conteggio={{ nome: t('parametri') }}
              columns={[
                {
                  key: 'chosen', header: <span aria-label={t('Scelto')} />, sort: false, filter: false, searchable: false,
                  render: (p) => {
                    const scelto = p.key in scelti
                    return (
                      <input
                        type="checkbox"
                        aria-label={etichettaParametro(p)}
                        checked={scelto}
                        disabled={!p.available}
                        onChange={() => {
                          const dopo = { ...scelti }
                          if (scelto) delete dopo[p.key]
                          else dopo[p.key] = p.interval_s
                          onCambia(dopo)
                        }}
                      />
                    )
                  },
                },
                { key: 'param', header: t('Parametro'), value: testoParametro, render: (p) => <CellaParametro p={p} /> },
                { key: 'method', header: t('Metodo'), value: (p) => etichettaMetodo(p) },
                {
                  key: 'interval', header: t('Frequenza'), value: (p) => etichettaFrequenza(scelti[p.key] ?? p.interval_s),
                  render: (p) => (
                    <select
                      aria-label={t('Frequenza di {parametro}', { parametro: etichettaParametro(p) })}
                      value={scelti[p.key] ?? p.interval_s}
                      disabled={!(p.key in scelti)}
                      onChange={(e) => onCambia({ ...scelti, [p.key]: Number(e.target.value) })}
                    >
                      {intervalli.map((s) => (
                        <option key={s} value={s}>{etichettaFrequenza(s)}</option>
                      ))}
                    </select>
                  ),
                },
              ]}
            />
          </div>
        )
      })}
    </>
  )
}

// ----------------------------------------------------------------- attivo

type Riga = { enabled: boolean; interval_s: number; warn: string; crit: string }

/**
 * Switch on or off, in one click, the parameters the table's filters leave:
 * «Spegni i 31 filtrati» after filtering the state column on «non risponde»,
 * or the shortcut «Spegni i 31 che non rispondono» without touching the
 * filters. Only the rows that can be read get switched on.
 */
function SelezioneInBlocco({ filtrate, totale, righe, onCambia }: {
  filtrate: EagleParam[]
  totale: number
  righe: Record<string, Riga>
  onCambia: (lista: EagleParam[], acceso: boolean) => void
}) {
  const daAccendere = filtrate.filter((p) => p.available && !righe[p.key]?.enabled)
  const daSpegnere = filtrate.filter((p) => righe[p.key]?.enabled)
  const nonRispondono = filtrate.filter((p) => p.status === 'down' && righe[p.key]?.enabled)
  const filtrata = filtrate.length !== totale
  return (
    <div className="eagle-blocco">
      <button type="button" className="secondary eagle-piccolo" disabled={daAccendere.length === 0}
              onClick={() => onCambia(daAccendere, true)}>
        {filtrata ? t('Accendi i {n} filtrati', { n: daAccendere.length }) : t('Accendi tutti ({n})', { n: daAccendere.length })}
      </button>
      <button type="button" className="secondary eagle-piccolo" disabled={daSpegnere.length === 0}
              onClick={() => onCambia(daSpegnere, false)}>
        {filtrata ? t('Spegni i {n} filtrati', { n: daSpegnere.length }) : t('Spegni tutti ({n})', { n: daSpegnere.length })}
      </button>
      {nonRispondono.length > 0 && (
        <button type="button" className="secondary eagle-piccolo" onClick={() => onCambia(nonRispondono, false)}>
          {t('Spegni i {n} che non rispondono', { n: nonRispondono.length })}
        </button>
      )}
      <span className="muted eagle-piccolo">
        {filtrata
          ? t('I pulsanti agiscono sulle {n} righe lasciate dai filtri della tabella.', { n: filtrate.length })
          : t('Filtra la tabella (per esempio Stato = «non risponde») e i pulsanti agiscono solo su quelle righe.')}
      </span>
    </div>
  )
}

function Attivo({
  ciId, dati, onDati, puoModificare, onRicarica,
}: {
  ciId: string
  dati: EagleCI
  onDati: (d: EagleCI) => void
  puoModificare: boolean
  onRicarica: () => Promise<void>
}) {
  const params = useMemo(() => dati.params ?? [], [dati.params])
  const intervalli = dati.intervals ?? []
  const [tutti, setTutti] = useState(false)
  const [aperto, setAperto] = useState<string | null>(null)
  const [modifica, setModifica] = useState(false)
  // Le letture arrivano ogni minuto: la sezione si aggiorna da sola, ma non
  // mentre si sta modificando (si perderebbero le modifiche).
  useEffect(() => {
    if (modifica) return
    const id = window.setInterval(() => {
      void onRicarica()
    }, 60_000)
    return () => window.clearInterval(id)
  }, [modifica, onRicarica])
  const [righe, setRighe] = useState<Record<string, Riga>>({})
  const [errore, setErrore] = useState<unknown>(null)
  const [invio, setInvio] = useState(false)
  // The rows each group's table shows after its filters: what «Accendi» and
  // «Spegni … i filtrati» act on. A switch with 66 interfaces, 31 down: one
  // filter on the state column and one click, instead of 31 checkboxes.
  const [viste, setViste] = useState<Record<string, EagleParam[]>>({})

  useEffect(() => {
    setRighe(Object.fromEntries(params.map((p) => [p.key, {
      enabled: p.enabled, interval_s: p.interval_s,
      warn: p.warn == null ? '' : String(p.warn), crit: p.crit == null ? '' : String(p.crit),
    }])))
  }, [params])

  const accesi = params.filter((p) => p.enabled)
  const visibili = tutti || modifica ? params : accesi
  // Per group, memoised: the table reports its filtered rows on every change
  // of its input, and a fresh array on every render would be a loop.
  const gruppi = useMemo(() => {
    const out: Record<string, EagleParam[]> = {}
    for (const g of GRUPPI) out[g.key] = visibili.filter((p) => p.group === g.key && righe[p.key])
    return out
  }, [visibili, righe]) // eslint-disable-line react-hooks/exhaustive-deps
  const prof = profilo(dati.profile?.key ?? 'base')

  async function salva() {
    setInvio(true)
    setErrore(null)
    try {
      const scelte: EagleScelta[] = params
        .filter((p) => p.available || righe[p.key]?.enabled === false)
        .map((p) => {
          const r = righe[p.key]
          return {
            key: p.key, enabled: r.enabled, interval_s: r.interval_s,
            warn: r.warn === '' ? null : Number(r.warn), crit: r.crit === '' ? null : Number(r.crit),
          }
        })
        .filter((s) => params.find((p) => p.key === s.key)?.group !== 'non_piu')
        // Only the rows that changed: the Control Plane updates the rows it
        // receives and leaves the others, and the whole table of a switch
        // with hundreds of interfaces exceeds what the WAF accepts in one
        // request (rule 200007, a thousand JSON arguments: «Errore 400»).
        .filter((s) => {
          const p = params.find((x) => x.key === s.key)
          return !p || p.enabled !== s.enabled || p.interval_s !== s.interval_s
            || (p.warn ?? null) !== (s.warn ?? null) || (p.crit ?? null) !== (s.crit ?? null)
        })
      if (scelte.length > 0) onDati(await api.ciEagleAggiorna(ciId, scelte))
      setModifica(false)
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  async function togli(key: string) {
    if (!window.confirm(t('Togliere questo parametro dal monitoraggio del CI? Il suo storico non si legge più. Non si può annullare.'))) return
    setInvio(true)
    setErrore(null)
    try {
      onDati(await api.ciEagleTogli(ciId, key))
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  async function disattiva() {
    if (!window.confirm(t('Spegnere il monitoraggio di questo CI? Non si può annullare: i parametri andranno riaccesi.'))) return
    setInvio(true)
    setErrore(null)
    try {
      onDati(await api.ciEagleDisattiva(ciId))
    } catch (e) {
      setErrore(e)
    } finally {
      setInvio(false)
    }
  }

  return (
    <Card
      title={t('Eagle · monitoraggio')}
      actions={
        puoModificare ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {modifica ? (
              <>
                <button type="button" onClick={salva} disabled={invio}>{invio ? t('Salvo…') : t('Salva')}</button>
                <button type="button" className="secondary" onClick={() => setModifica(false)} disabled={invio}>{t('Annulla')}</button>
              </>
            ) : (
              <>
                <button type="button" className="secondary" onClick={() => setModifica(true)}>{t('Modifica')}</button>
                <button type="button" className="secondary" onClick={disattiva} disabled={invio}>{t('Disattiva')}</button>
              </>
            )}
          </div>
        ) : undefined
      }
    >
      <p style={{ marginTop: 0 }} className="muted">
        {dati.mode === 'profile'
          ? t('Attivo con il profilo «{profilo}»', { profilo: prof.label })
          : t('Attivo con una scelta manuale')}
        {dati.activated_at && <> · {t('dal {quando}', { quando: formatDateTime(dati.activated_at) })}</>}
        {dati.profile_applied && <> · {t('profilo «{nome}»', { nome: dati.profile_applied.name })}</>}
        {' · '}{t('{n} parametri monitorati', { n: accesi.length })}
      </p>
      {dati.points && <EaglePunti ciId={ciId} punti={dati.points} puoModificare={puoModificare} onDati={onDati} />}
      <EagleAssegnazione ciId={ciId} dati={dati} puoModificare={puoModificare} onDati={onDati} />
      <NotaAgente dati={dati} />
      <ErrorBanner error={errore} />
      {!modifica && (
        <p>
          <label className="eagle-interruttore">
            <input type="checkbox" checked={tutti} onChange={(e) => setTutti(e.target.checked)} />
            {' '}{t('Mostra tutti i parametri monitorabili ({n})', { n: params.length })}
          </label>
        </p>
      )}
      {GRUPPI.map((g) => {
        const gruppo = gruppi[g.key] ?? []
        if (gruppo.length === 0) return null
        const cambia = (p: EagleParam, parte: Partial<Riga>) => setRighe({ ...righe, [p.key]: { ...righe[p.key], ...parte } })
        return (
          <div key={g.key} className="eagle-gruppo">
            <h4>{g.label()}</h4>
            {modifica && (
              <SelezioneInBlocco filtrate={viste[g.key] ?? gruppo} totale={gruppo.length} righe={righe}
                                 onCambia={(lista, acceso) => {
                                   const dopo = { ...righe }
                                   for (const p of lista) {
                                     if (acceso && !p.available) continue
                                     dopo[p.key] = { ...dopo[p.key], enabled: acceso }
                                   }
                                   setRighe(dopo)
                                 }} />
            )}
            <DataTable
              rows={gruppo}
              onViste={(r) => setViste((v) => {
                const prima = v[g.key]
                if (prima && prima.length === r.length && prima.every((p, i) => p === r[i])) return v
                return { ...v, [g.key]: r }
              })}
              rowKey={(p) => p.key}
              rowClassName={(p) => (p.available ? undefined : 'eagle-riga--spenta')}
              conteggio={{ nome: t('parametri') }}
              expanded={(p) => aperto === p.key && !modifica}
              expand={(p) => (
                <SerieParametro ciId={ciId} param={p}
                                principale={dati.points?.effective.find((x) => x.role === 'primary')?.appliance_id}
                                nomiSonde={Object.fromEntries((dati.points?.effective ?? []).map((x) => [x.appliance_id, x.name]))} />
              )}
              columns={[
                ...(modifica ? [{
                  key: 'chosen', header: <span aria-label={t('Scelto')} />, sort: false, filter: false, searchable: false,
                  render: (p: EagleParam) => {
                    const r = righe[p.key]
                    return (
                      <input type="checkbox" aria-label={etichettaParametro(p)} checked={r.enabled}
                             disabled={!p.available && !r.enabled} onChange={() => cambia(p, { enabled: !r.enabled })} />
                    )
                  },
                }] : []),
                {
                  key: 'param', header: t('Parametro'), value: testoParametro,
                  render: (p) => (
                    <>
                      <CellaParametro p={p} />
                      {p.extra.custom && puoModificare && !modifica && (
                        <div>
                          <button type="button" className="linklike eagle-piccolo" disabled={invio}
                                  onClick={() => void togli(p.key)}>
                            {t('Togli')}
                          </button>
                        </div>
                      )}
                    </>
                  ),
                },
                { key: 'method', header: t('Metodo'), value: (p) => etichettaMetodo(p) },
                {
                  key: 'interval', header: t('Frequenza'), value: (p) => etichettaFrequenza(righe[p.key].interval_s),
                  render: (p) => {
                    const r = righe[p.key]
                    return modifica ? (
                      <select aria-label={t('Frequenza di {parametro}', { parametro: etichettaParametro(p) })}
                              value={r.interval_s} disabled={!r.enabled}
                              onChange={(e) => cambia(p, { interval_s: Number(e.target.value) })}>
                        {intervalli.map((s) => (
                          <option key={s} value={s}>{etichettaFrequenza(s)}</option>
                        ))}
                      </select>
                    ) : (
                      etichettaFrequenza(p.interval_s)
                    )
                  },
                },
                {
                  key: 'warn', header: t('Attenzione'), value: (p) => p.warn,
                  render: (p) => {
                    const r = righe[p.key]
                    return modifica ? (
                      <SogliaInput p={p} valore={r.warn} disabled={!r.enabled} onCambia={(v) => cambia(p, { warn: v })}
                                   etichetta={t('Soglia di attenzione di {parametro}', { parametro: etichettaParametro(p) })} />
                    ) : soglia(p, p.warn)
                  },
                },
                {
                  key: 'crit', header: t('Critico'), value: (p) => p.crit,
                  render: (p) => {
                    const r = righe[p.key]
                    return modifica ? (
                      <SogliaInput p={p} valore={r.crit} disabled={!r.enabled} onCambia={(v) => cambia(p, { crit: v })}
                                   etichetta={t('Soglia critica di {parametro}', { parametro: etichettaParametro(p) })} />
                    ) : soglia(p, p.crit)
                  },
                },
                {
                  key: 'status', header: t('Stato'),
                  value: (p) => (!p.enabled ? t('non monitorato') : p.status ? etichettaStato(p.status) : t('in attesa dei primi dati')),
                  render: (p) => (
                    !p.enabled ? (
                      <span className="muted">{t('non monitorato')}</span>
                    ) : p.status ? (
                      <button type="button" className="linklike eagle-stato"
                              aria-expanded={aperto === p.key}
                              title={t('Mostra le ultime 24 ore')}
                              onClick={() => setAperto(aperto === p.key ? null : p.key)}>
                        <StatoBadge stato={p.status} />{' '}
                        {p.last_value != null && <span className="mono">{formatValore(p.last_value)}{p.unit ? ` ${p.unit}` : ''}</span>}
                        {p.last_at && <span className="muted eagle-piccolo"> · {timeAgo(p.last_at)}</span>}
                        {p.status_since && p.status !== 'ok' && p.status_since !== p.last_at && (
                          <span className="muted eagle-piccolo"> · {t('da {quando}', { quando: timeAgo(p.status_since) })}</span>
                        )}
                        {p.last_error && <div className="eagle-motivo">{p.last_error}</div>}
                        {(p.points ?? []).length > 0 && (
                          <ul className="eagle-per-punto">
                            {(p.points ?? []).map((x) => (
                              <li key={x.appliance_id}>
                                {x.name}: <StatoBadge stato={x.status ?? ''} />
                                {x.last_value != null && <span className="mono"> {formatValore(x.last_value)}{p.unit ? ` ${p.unit}` : ''}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </button>
                    ) : (
                      <span className="muted">{t('in attesa dei primi dati')}</span>
                    )
                  ),
                },
              ]}
            />
          </div>
        )
      })}
      {puoModificare && !modifica && <EagleNuovo ciId={ciId} params={params} onDati={onDati} />}
    </Card>
  )
}

/**
 * Perché un parametro dell'agente aspetta: l'agente non è legato al CI, o
 * non si fa sentire, o è vecchio. Si mostra solo quando serve.
 */
function NotaAgente({ dati }: { dati: EagleCI }) {
  const a = dati.agent
  if (!a) return null
  const inAttesa = (dati.params ?? []).some((p) => p.method === 'agent' && p.enabled && !p.status)
  if (!inAttesa) return null
  const silenzio = a.last_keepalive_at ? Date.now() - new Date(a.last_keepalive_at).getTime() > 3 * 60_000 : true
  return (
    <p className="eagle-motivo" style={{ margin: '0 0 12px' }}>
      {!a.linked
        ? t('Nessun agente è ancora legato a questo CI: l’agente si lega alla prossima consegna d’inventario (Agenti d’inventario → «Inventario adesso»).')
        : silenzio
          ? t('L’agente «{nome}» non si fa sentire da {quando}: le letture arrivano con il keep-alive di ogni minuto. Controlla che sia residente e aggiornato.', { nome: a.name ?? '', quando: a.last_keepalive_at ? timeAgo(a.last_keepalive_at) : t('sempre') })
          : t('L’agente «{nome}» è in linea (versione {v}): le prime letture arrivano entro un paio di minuti. Se non arrivano, l’agente va aggiornato a una versione con Eagle.', { nome: a.name ?? '', v: a.agent_version ?? '—' })}
    </p>
  )
}

function SogliaInput({
  p, valore, disabled, onCambia, etichetta,
}: { p: EagleParam; valore: string; disabled: boolean; onCambia: (v: string) => void; etichetta: string }) {
  return (
    <span className="eagle-soglia">
      <input type="number" inputMode="decimal" value={valore} disabled={disabled} aria-label={etichetta}
             onChange={(e) => onCambia(e.target.value)} style={{ width: 80 }} />
      {p.unit && <span className="muted"> {p.unit}</span>}
    </span>
  )
}
